import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MapPin, Camera, LogIn, LogOut, ListChecks, TrendingUp, Sparkles, Loader2, ChefHat } from "lucide-react";
import { toast } from "sonner";

const SHIFT_BADGE = {
  scheduled: { l: "Chưa mở", c: "bg-stone-200 text-stone-800" },
  open: { l: "Đang mở", c: "bg-emerald-100 text-emerald-900" },
  closed: { l: "Đã đóng", c: "bg-stone-100 text-stone-500" },
};
const SHIFT_ROLES = ["kitchen", "restaurant", "manager"];

export default function StaffHome() {
  const [data, setData] = useState(null);
  const [att, setAtt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [shiftBusy, setShiftBusy] = useState(false);
  const [closing, setClosing] = useState(null);
  const [closingNote, setClosingNote] = useState("");
  const nav = useNavigate();
  const { user } = useAuth();
  const showShift = SHIFT_ROLES.includes(user?.role);

  const loadShifts = useCallback(() => {
    if (!showShift) return;
    api.get("/hrm/shifts/my-today").then((r) => setShifts(r.data)).catch(() => {});
  }, [showShift]);

  const load = () => {
    api.get("/dashboard/staff").then((r) => setData(r.data));
    api.get("/attendance/today").then((r) => setAtt(r.data));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadShifts(); }, [loadShifts]);

  const openShift = async (s) => {
    setShiftBusy(true);
    try {
      await api.post(`/hrm/shifts/${s.id}/open`);
      toast.success("Đã mở ca bếp");
      loadShifts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lỗi"); }
    finally { setShiftBusy(false); }
  };

  const confirmClose = async () => {
    setShiftBusy(true);
    try {
      await api.post(`/hrm/shifts/${closing.id}/close`, { closing_note: closingNote || undefined });
      toast.success("Đã đóng ca bếp");
      setClosing(null); setClosingNote(""); loadShifts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lỗi"); }
    finally { setShiftBusy(false); }
  };

  const checkin = async () => {
    setBusy(true);
    let coords = null;
    try {
      if (navigator.geolocation) {
        coords = await new Promise((res) => {
          navigator.geolocation.getCurrentPosition(
            (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
            () => res(null),
            { timeout: 5000 }
          );
        });
      }
      await api.post("/attendance/check-in", coords || {});
      toast.success("Đã check-in 👍"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lỗi"); }
    finally { setBusy(false); }
  };

  const checkout = async () => {
    setBusy(true);
    let coords = null;
    try {
      if (navigator.geolocation) {
        coords = await new Promise((res) => {
          navigator.geolocation.getCurrentPosition(
            (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
            () => res(null), { timeout: 5000 }
          );
        });
      }
      await api.post("/attendance/check-out", coords || {});
      toast.success("Đã tan ca 🏠"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lỗi"); }
    finally { setBusy(false); }
  };

  if (!data) {
    return <div className="p-6 text-center text-stone-400">Đang tải...</div>;
  }

  return (
    <div data-testid="staff-home" className="space-y-4 p-4">
      {/* Greeting */}
      <div className="bg-white border border-stone-200 rounded-sm p-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 font-bold">
          {new Date().toLocaleDateString("vi-VN", { weekday: "long", day: "numeric", month: "long" })}
        </div>
        <h1 className="font-serif text-2xl font-bold mt-1">
          Chào, {data.employee?.full_name?.split(" ").slice(-1)[0] || "bạn"}.
        </h1>
        {data.employee?.position && <p className="text-sm text-stone-600 mt-0.5">{data.employee.position}</p>}
      </div>

      {/* Check-in/out big CTA */}
      <div className="bg-white border border-stone-200 rounded-sm p-4">
        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-1">Chấm công hôm nay</div>
        {att?.checked_in && (
          <div className="text-sm text-stone-700 mb-2">
            ✓ Check-in: <span className="font-semibold">{fmtDateTime(att.check_in_at)}</span>
            {att.checked_out && <div className="mt-1">✓ Tan ca: <span className="font-semibold">{fmtDateTime(att.check_out_at)}</span></div>}
          </div>
        )}
        {!att?.checked_in ? (
          <Button onClick={checkin} disabled={busy} className="w-full h-14 bg-emerald-800 hover:bg-emerald-900 rounded-sm text-base font-bold" data-testid="btn-checkin">
            {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <LogIn className="w-5 h-5 mr-2" />}
            VÀO CA
          </Button>
        ) : !att?.checked_out ? (
          <Button onClick={checkout} disabled={busy} className="w-full h-14 bg-amber-700 hover:bg-amber-800 rounded-sm text-base font-bold" data-testid="btn-checkout">
            {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <LogOut className="w-5 h-5 mr-2" />}
            TAN CA
          </Button>
        ) : (
          <div className="text-center py-4 text-emerald-800 font-bold">
            ✓ Hôm nay đã hoàn thành ca làm
          </div>
        )}
        <div className="text-[10px] text-stone-400 mt-2 text-center flex items-center justify-center gap-1">
          <MapPin className="w-3 h-3" /> Hệ thống sẽ ghi GPS khi bạn bấm
        </div>
      </div>

      {/* Kitchen shift open/close (#98) */}
      {showShift && (
        <div className="bg-white border border-stone-200 rounded-sm p-4" data-testid="shift-card">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2 flex items-center gap-1">
            <ChefHat className="w-3.5 h-3.5 text-amber-700" /> Ca bếp hôm nay
          </div>
          {shifts.length === 0 && <div className="text-sm text-stone-400">Hôm nay bạn không có ca nào.</div>}
          <div className="space-y-2">
            {shifts.map((s) => {
              const badge = SHIFT_BADGE[s.status] || SHIFT_BADGE.scheduled;
              return (
                <div key={s.id} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-stone-900 truncate">{s.shift_name}</div>
                    <div className="text-xs text-stone-500">
                      {s.start_time}–{s.end_time}{s.branch_name ? ` · ${s.branch_name}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold ${badge.c}`} data-testid="shift-status-badge">{badge.l}</span>
                    {s.status === "scheduled" && (
                      <Button onClick={() => openShift(s)} disabled={shiftBusy} className="h-9 bg-emerald-700 hover:bg-emerald-800 rounded-sm text-xs tap-target" data-testid="btn-open-shift">
                        Mở ca bếp
                      </Button>
                    )}
                    {s.status === "open" && (
                      <Button onClick={() => { setClosing(s); setClosingNote(""); }} disabled={shiftBusy} className="h-9 bg-red-700 hover:bg-red-800 rounded-sm text-xs tap-target" data-testid="btn-close-shift">
                        Đóng ca bếp
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={!!closing} onOpenChange={(o) => !o && setClosing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">Đóng ca bếp</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600">Xác nhận đóng ca <strong>{closing?.shift_name}</strong>?</p>
          <Textarea value={closingNote} onChange={(e) => setClosingNote(e.target.value)} rows={3} placeholder="Ghi chú đóng ca (tuỳ chọn)..." className="rounded-sm" data-testid="shift-closing-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosing(null)} className="rounded-sm">Huỷ</Button>
            <Button onClick={confirmClose} disabled={shiftBusy} className="rounded-sm bg-red-700 hover:bg-red-800" data-testid="btn-confirm-close">Đóng ca</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick tiles */}
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => nav("/staff/tasks")} className="bg-white border border-stone-200 rounded-sm p-4 text-left active:bg-stone-50" data-testid="quick-tasks">
          <div className="flex items-center justify-between mb-1">
            <ListChecks className="w-5 h-5 text-emerald-800" />
            <span className="font-serif text-2xl font-bold">{data.today_tasks}</span>
          </div>
          <div className="text-xs font-semibold text-stone-700">Việc cần xử lý</div>
        </button>
        <button onClick={() => nav("/staff/kpi")} className="bg-white border border-stone-200 rounded-sm p-4 text-left active:bg-stone-50" data-testid="quick-kpi">
          <div className="flex items-center justify-between mb-1">
            <TrendingUp className="w-5 h-5 text-amber-700" />
            <span className="font-serif text-2xl font-bold">{data.kpi_score}<span className="text-xs">%</span></span>
          </div>
          <div className="text-xs font-semibold text-stone-700">KPI trung bình</div>
        </button>
      </div>

      {/* Highlight */}
      <div className="bg-gradient-to-br from-emerald-900 to-emerald-800 text-white rounded-sm p-4">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-amber-300 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold">Tip nhanh</div>
            <div className="text-sm mt-0.5">Bấm thẻ <strong>Việc</strong> để xem chi tiết SOP và đánh dấu hoàn thành ngay tại điểm bán.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
